/** Papermill's `extraction.json`, and the graph and field views drawn from it. */

export interface BBox {
  page: number;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Where in a recording a value was said, and by whom: a transcript's `BBox`. */
export interface TimeSpan {
  /** The transcript window it was read from. */
  page: number;
  track_id: string;
  start_ms: number;
  end_ms: number;
  speaker?: string | null;
  segment_ids?: string[];
}

export interface Occurrence {
  value: string;
  confidence: number;
  page: number;
  bbox: BBox | null;
  span?: TimeSpan | null;
  source: string;
  evidence: string;
  ungrounded: boolean;
}

export interface FieldValue {
  value: string;
  corrected_value?: string | null;
  confidence: number;
  bbox?: BBox | null;
  span?: TimeSpan | null;
  source?: string;
  evidence?: string;
  ungrounded?: boolean;
  occurrences?: Occurrence[];
}

export interface Entity {
  id: string;
  type: string;
  fields: Record<string, FieldValue>;
  discarded?: boolean;
}

export interface Edge {
  type: string;
  source_id: string;
  target_id: string;
  attributes?: Record<string, FieldValue>;
}

export interface TranscriptSpeaker {
  entity_id: string;
  track_id: string;
  label?: string | null;
  start_ms?: number;
  end_ms?: number;
  talk_time_ms?: number;
  segment_count?: number;
  declared_role?: string | null;
  inferred_role?: string | null;
}

export interface TranscriptProvenance {
  duration_ms?: number;
  timing?: string;
  locale?: string;
  speakers?: TranscriptSpeaker[];
}

export interface Extraction {
  schema_version?: number;
  extraction_config_version?: string;
  crc32c?: string;
  page_count?: number;
  source_kind?: 'document' | 'transcript';
  transcript?: TranscriptProvenance | null;
  entities?: Entity[];
  edges?: Edge[];
}

export type Category =
  'person' | 'organization' | 'place' | 'money' | 'work' | 'identity' | 'document';

const CATEGORY_OF: Record<string, Category> = {
  person: 'person',
  organization: 'organization',
  address: 'place',
  property: 'place',
  tenancy: 'place',
  valuation: 'place',
  property_insurance_policy: 'place',
  financial_account: 'money',
  financial_statement: 'money',
  transaction: 'money',
  credit_card: 'money',
  term_loan: 'money',
  asset_finance: 'money',
  overdraft: 'money',
  gift: 'money',
  defined_benefit_pension: 'money',
  defined_contribution_pension: 'money',
  protection_policy: 'money',
  employment: 'work',
  income_source: 'work',
  pay_statement: 'work',
  tax_assessment: 'work',
  passport: 'identity',
  driving_licence: 'identity',
  residence_permit: 'identity',
  national_id_card: 'identity',
  visa: 'identity',
  document: 'document',
  mortgage_case: 'document',
};

export const CATEGORY_LABELS: Record<Category, string> = {
  person: 'People',
  organization: 'Organisations',
  place: 'Places & property',
  money: 'Accounts & money',
  work: 'Work & income',
  identity: 'Identity',
  document: 'Documents',
};

export function categoryOf(type: string): Category {
  return CATEGORY_OF[type] ?? 'document';
}

/** `financial_account` → "Financial account"; `dateOfBirth` → "Date of birth". */
export function humanize(key: string): string {
  const words = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Two letters for a type's circle: "Financial account" → "FA", "Passport" → "PA". */
export function glyphOf(typeLabel: string): string {
  const words = typeLabel.split(' ');
  return (words.length > 1 ? words[0][0] + words[1][0] : typeLabel.slice(0, 2)).toUpperCase();
}

const LABEL_FIELDS = [
  'fullName',
  'name',
  'legalName',
  'tradingName',
  'employerName',
  'accountName',
  'fullAddress',
  'addressLine1',
  'line1',
  'jobTitle',
  'description',
  'documentNumber',
  'accountNumber',
  'title',
];

export function valueOf(field: FieldValue): string {
  return field.corrected_value || field.value;
}

/** A short name for an entity: its most name-like field, else its type. */
export function entityLabel(entity: Entity): string {
  const fields = entity.fields ?? {};
  const key =
    LABEL_FIELDS.find((name) => fields[name]?.value) ??
    Object.keys(fields).find((name) => /name/i.test(name) && fields[name]?.value);
  const raw = key ? valueOf(fields[key]) : '';
  const label = raw.trim() || humanize(entity.type);
  return label.length > 32 ? `${label.slice(0, 31)}…` : label;
}

export interface GraphNode {
  id: string;
  type: string;
  typeLabel: string;
  label: string;
  category: Category;
  fieldCount: number;
}

export interface GraphLink {
  id: string;
  source: string;
  target: string;
  type: string;
  label: string;
}

export interface Graph {
  nodes: GraphNode[];
  links: GraphLink[];
}

export function toGraph(extraction: Extraction): Graph {
  const entities = (extraction.entities ?? []).filter((entity) => !entity.discarded && entity.id);
  const ids = new Set(entities.map((entity) => entity.id));
  const nodes = entities.map((entity) => ({
    id: entity.id,
    type: entity.type,
    typeLabel: humanize(entity.type),
    label: entityLabel(entity),
    category: categoryOf(entity.type),
    fieldCount: Object.keys(entity.fields ?? {}).length,
  }));
  const links = (extraction.edges ?? [])
    .filter((edge) => ids.has(edge.source_id) && ids.has(edge.target_id))
    .map((edge, index) => ({
      id: `${edge.source_id}-${edge.type}-${edge.target_id}-${index}`,
      source: edge.source_id,
      target: edge.target_id,
      type: edge.type,
      label: humanize(edge.type).toLowerCase(),
    }));
  return { nodes, links };
}

export interface FieldRow {
  key: string;
  label: string;
  value: string;
  corrected: boolean;
  confidence: number;
  source: string;
  evidence: string;
  grounded: boolean;
  /** Every located sighting, representative first: a box on a page, or a span of audio. */
  sightings: Sighting[];
}

export type Sighting =
  | { page: number; bbox: BBox; span?: undefined; evidence: string; value: string }
  | { page: number; span: TimeSpan; bbox?: undefined; evidence: string; value: string };

function sightingOf(located: {
  bbox?: BBox | null;
  span?: TimeSpan | null;
  evidence?: string;
  value: string;
}): Sighting | null {
  const evidence = located.evidence ?? '';
  if (located.bbox) {
    return { page: located.bbox.page, bbox: located.bbox, evidence, value: located.value };
  }
  if (located.span) {
    return { page: located.span.page, span: located.span, evidence, value: located.value };
  }
  return null;
}

function sameSighting(a: Sighting, b: Sighting): boolean {
  if (a.bbox && b.bbox) {
    return sameBox(a.bbox, b.bbox);
  }
  if (a.span && b.span) {
    return (
      a.span.track_id === b.span.track_id &&
      a.span.start_ms === b.span.start_ms &&
      a.span.end_ms === b.span.end_ms
    );
  }
  return false;
}

export function fieldRows(fields: Record<string, FieldValue>): FieldRow[] {
  return Object.entries(fields ?? {})
    .filter(([, field]) => field && `${field.value ?? ''}`.trim() !== '')
    .map(([key, field]) => {
      const sightings = (field.occurrences ?? [])
        .map(sightingOf)
        .filter((sighting): sighting is Sighting => sighting !== null);
      const representative = sightingOf(field);
      if (representative && !sightings.some((s) => sameSighting(s, representative))) {
        sightings.unshift(representative);
      }
      return {
        key,
        label: humanize(key),
        value: valueOf(field),
        corrected: Boolean(field.corrected_value),
        confidence: field.confidence ?? 0,
        source: field.source ?? 'model',
        evidence: field.evidence ?? '',
        grounded: !field.ungrounded && sightings.length > 0,
        sightings,
      };
    });
}

function sameBox(a: BBox, b: BBox): boolean {
  return a.page === b.page && a.x0 === b.x0 && a.y0 === b.y0 && a.x1 === b.x1 && a.y1 === b.y1;
}

/** Grow a box a little each way, so a slightly misplaced reading still sits inside it. */
export function padBox(bbox: BBox): BBox {
  const padX = Math.max(0.012, (bbox.x1 - bbox.x0) * 0.15);
  const padY = Math.max(0.006, (bbox.y1 - bbox.y0) * 0.35);
  return {
    page: bbox.page,
    x0: Math.max(0, bbox.x0 - padX),
    y0: Math.max(0, bbox.y0 - padY),
    x1: Math.min(1, bbox.x1 + padX),
    y1: Math.min(1, bbox.y1 + padY),
  };
}

export const SOURCE_LABELS: Record<string, string> = {
  text_layer: 'Text layer',
  ocr: 'OCR',
  model: 'Inferred',
  detector: 'Detector',
  transcript: 'Heard',
};

export interface Relationship {
  edge: Edge;
  label: string;
  otherId: string;
  direction: 'out' | 'in';
}

export function relationshipsOf(entityId: string, edges: Edge[]): Relationship[] {
  return edges.flatMap((edge): Relationship[] => {
    if (edge.source_id === entityId) {
      return [
        {
          edge,
          label: humanize(edge.type).toLowerCase(),
          otherId: edge.target_id,
          direction: 'out' as const,
        },
      ];
    }
    if (edge.target_id === entityId) {
      return [
        {
          edge,
          label: humanize(edge.type).toLowerCase(),
          otherId: edge.source_id,
          direction: 'in' as const,
        },
      ];
    }
    return [];
  });
}
